import React, { useState, useEffect, useRef } from 'react';
import './DetailOffer.css';
import axios from 'axios';
import { Info, Check, X, Download, Pencil, FileSearch } from 'lucide-react';
import ComponentCard from '../component/ComponentCard';
import CatalogAssetIcons from './CatalogAssetIcons';
import CatalogRowPreview from './CatalogRowPreview';
import DocumentCharacteristicsButton from './DocumentCharacteristicsButton';
import useCatalogAssetAvailability from '../hooks/useCatalogAssetAvailability';
import * as XLSX from 'xlsx';



// [[Site Parser Registry]] Render product URLs as links while keeping the rest of the remark as plain text.
const RemarkText = ({ text }) => String(text ?? '').split(/(https?:\/\/[^\s<>"'|]+)/gi).map((part, index) => {
    if (!/^https?:\/\//i.test(part)) return part;
    const url = part.replace(/[.,;!?]+$/, '');
    try {
        new URL(url);
    } catch {
        return part;
    }
    return <React.Fragment key={index}>
        <a href={url} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()}
           style={{ color: 'var(--primary)', overflowWrap: 'anywhere', textDecoration: 'underline' }}>
            {url}
        </a>{part.slice(url.length)}
    </React.Fragment>;
});

const DetailOffer = ({ isOpen, selectedFileIds, onClose, numberOffer, highlightRowId = null, onOpenOriginal, catalogLinks = false }) => {
    const [data, setData] = useState([]);
    const catalogAssets = useCatalogAssetAvailability(data, isOpen, catalogLinks);
    const [isLoading, setIsLoading] = useState(false);
    const [refreshLoading, setRefreshLoading] = useState(false);

    const [editingId, setEditingId] = useState(null);
    const [editingValue, setEditingValue] = useState('');

    const [selectedComponent, setSelectedComponent] = useState(null);
    const [componentInfo, setComponentInfo] = useState(null);
    const [infoLoading, setInfoLoading] = useState(false);
    const highlightedRowRef = useRef(null);

    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    // [[Site Parser Registry]] Prices are stored and displayed in USD with four decimal places.
    const round = (n) => n == null ? null : Math.round(Number(n) * 10000) / 10000;
    const formatPrice = (n) => n == null ? '—' : Number(n).toFixed(4);

    const [filters, setFilters] = useState({
        error: true,      // 🔴
        warning: true,    // 🟡
        changed: true,    // 🔵
        normal: true      // ⚪ 
    });


    const filteredData = data.filter(item => {
        if (item.has_error === 1 && !filters.error) return false;
        if (item.has_error === 2 && !filters.warning) return false;
        if (item.is_changed && !filters.changed) return false;

        const isNormal =
            item.has_error === 0 && !item.is_changed;

        if (isNormal && !filters.normal) return false;

        return true;
    });

    useEffect(() => {
        if (!isOpen || !highlightRowId || !data.length) return;
        requestAnimationFrame(() => highlightedRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
    }, [data, highlightRowId, isOpen]);

    // === REFRESH COMPONENT ===




    const counters = {
        error: data.filter(i => i.has_error === 1).length,
        warning: data.filter(i => i.has_error === 2).length,
        changed: data.filter(i => i.is_changed).length,
        normal: data.filter(
            i => i.has_error === 0 && !i.is_changed
        ).length
    };




    // Нормализуем dev/prod поля в один формат
    const normalizeOffer = (item) => ({
        ...item,
        has_error: item.has_error ?? item.hasError ?? 0,
        is_changed: item.is_changed ?? item.changed ?? false,

        // веса тоже иногда snake/camel
        weight_string: item.weight_string ?? item.weightString ?? null,
        weight_total: item.weight_total ?? item.weightTotal ?? null,
    });

    // === SAVE PN ===
    const savePartNumber = async (row) => {
        try {
            await axios.post('/api/offers/update-partnumber', {
                id: row.id,
                new_partnumber: editingValue,
            });

            setData((prev) =>
                prev.map((it) =>
                    it.id === row.id
                        ? { ...it, name: editingValue, is_changed: true }
                        : it
                )
            );

            setEditingId(null);
            setEditingValue('');
        } catch (err) {
            console.error('Ошибка сохранения PN:', err);
        }
    };

    // === DOWNLOAD EXCEL ===
    const downloadExcel = () => {
        const rows = data.map((item) => ({
            'Part Number': item.name ?? '',
            Manufacturer: item.wrk ?? '',
            DC: item.dc ?? '',
            LT: item.lt ?? '',
            Quantity: item.cnt ?? '',
            Price: round(item.price),
            Remark: item.remark ?? '',
            Encryption: item.encryption ?? '',
            'Weight (g)': item.weight ?? '',
            'Weight total (g)': item.weight_total ?? '',
            'Weight original': item.weight_string ?? '',
        }));

        const ws = XLSX.utils.json_to_sheet(rows);
        rows.forEach((_, index) => {
            const cell = ws[`F${index + 2}`];
            if (cell) cell.z = '0.0000';
        });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Offers');
        XLSX.writeFile(wb, 'offer-details.xlsx');
    };

    // === INFO ===
    const fetchComponentInfo = async (partNumber) => {
        if (!partNumber) return;

        setSelectedComponent(partNumber);
        setInfoLoading(true);
        setComponentInfo(null);

        try {
            const res = await axios.post('/api/components/component-info', {
                part_number: partNumber,
            });
            setComponentInfo(res.data?.component ?? { error: 'Empty response' });
        } catch (error) {
            console.error('Ошибка загрузки:', error);
            setComponentInfo({ error: 'Не удалось загрузить данные' });
        } finally {
            setInfoLoading(false);
        }
    };

    const refreshComponentData = async () => {
        if (!selectedComponent) return;

        setRefreshLoading(true);

        try {
            // 1. обновляем данные на бэке
            await axios.post('/api/components/refresh-component', {
                part_number: selectedComponent,
            });

            // 2. заново запрашиваем информацию
            const res = await axios.post('/api/components/component-info', {
                part_number: selectedComponent,
            });

            setComponentInfo(res.data?.component ?? null);
        } catch (error) {
            console.error('Ошибка обновления компонента:', error);
        } finally {
            setRefreshLoading(false);
        }
    };

    // === LOAD DATA ===
    useEffect(() => {
        if (!isOpen || !selectedFileIds?.length) return;

        const abort = new AbortController();
        setIsLoading(true);

        const fetchData = async () => {
            const formData = new FormData();
            formData.append('offers', selectedFileIds);

            try {
                const response = await axios.post('/api/listFileOfferModal', formData, {
                    signal: abort.signal,
                });
                const list = Array.isArray(response.data) ? response.data : [];
                setData(list.map(normalizeOffer));
            } catch (error) {
                if (error.name !== 'AbortError') console.error(error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
        return () => abort.abort();
    }, [isOpen, selectedFileIds]);

    if (!isOpen) return null;

    return (
        <>
            {/* ================= MAIN MODAL ================= */}
            <div className="modal_backdrop" onClick={onClose}>
                <div className="modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modalContent">
                        <div className="modalHeader">
                            <h2>Детализация предложения {numberOffer}</h2>
                            <DocumentCharacteristicsButton key={Array.isArray(selectedFileIds) ? selectedFileIds[0] : selectedFileIds} kind="offer" documentId={Array.isArray(selectedFileIds) ? selectedFileIds[0] : selectedFileIds} disabled={isLoading || !data.length} />
                            {catalogAssets.error && <span role="status">Не удалось проверить изображения и PDF. <button type="button" onClick={catalogAssets.retry}>Повторить</button></span>}

                            <div className="headerActions">
                                {onOpenOriginal && (
                                    <button
                                        className="sourceBtn"
                                        onClick={onOpenOriginal}
                                        title="Посмотреть исходный документ"
                                    >
                                        <FileSearch size={16} />
                                        Исходник
                                    </button>
                                )}
                                <button
                                    className="excelBtn"
                                    onClick={downloadExcel}
                                    disabled={!data.length}
                                    title="Скачать Excel"
                                >
                                    <Download size={16} />
                                </button>

                                <button
                                    className="iconBtn"
                                    data-modal-close
                                    onClick={onClose}
                                    title="Закрыть"
                                >
                                    <X className="iconClose" />
                                </button>
                            </div>
                        </div>

                        <div className="legend">
                            <label className="legendItem">
                                <input
                                    type="checkbox"
                                    checked={filters.error}
                                    onChange={() =>
                                        setFilters(f => ({ ...f, error: !f.error }))
                                    }
                                />
                                <span className="legendRed" />
                                Нет информации ({counters.error})
                            </label>

                            <label className="legendItem">
                                <input
                                    type="checkbox"
                                    checked={filters.warning}
                                    onChange={() =>
                                        setFilters(f => ({ ...f, warning: !f.warning }))
                                    }
                                />
                                <span className="legendYellow" />
                                Не загружена информация ({counters.warning})
                            </label>

                            <label className="legendItem">
                                <input
                                    type="checkbox"
                                    checked={filters.changed}
                                    onChange={() =>
                                        setFilters(f => ({ ...f, changed: !f.changed }))
                                    }
                                />
                                <span className="legendBlue" />
                                PN изменён пользователем ({counters.changed})
                            </label>

                            <label className="legendItem">
                                <input
                                    type="checkbox"
                                    checked={filters.normal}
                                    onChange={() =>
                                        setFilters(f => ({ ...f, normal: !f.normal }))
                                    }
                                />
                                <span className="legendGray" />
                                Обработанные ({counters.normal})
                            </label>
                        </div>


                        <div className="modalBody">
                            {isLoading ? (
                                <div className="loadingContainer">
                                    <div className="spinner" />
                                    Загрузка данных...
                                </div>
                            ) : (
                                <>
                                    {/* ===== DESKTOP TABLE ===== */}
                                    {!isMobile && (
                                        <div className="tableContainer">
                                            <table className="table">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: 56 }}>Info</th>
                                                        <th>Part Number</th>
                                                        <th>Manufacturer</th>
                                                        <th>DC</th>
                                                        <th>LT</th>
                                                        <th>Quantity</th>
                                                        <th>Price, usd</th>
                                                        <th>Remark</th>
                                                        <th>overhead_cost</th>
                                                    </tr>
                                                </thead>

                                                <tbody>

                                                    {
                                                        filteredData.map(item => (
                                                            <tr
                                                                key={item.id}
                                                                ref={String(item.id) === String(highlightRowId) ? highlightedRowRef : null}
                                                                className={`${item.is_changed ? 'changedRow' : ''} ${String(item.id) === String(highlightRowId) ? 'selectedSearchRow' : ''}`}
                                                            >
                                                                <td>
                                                                    <button
                                                                        className="infoButton"
                                                                        onClick={() => fetchComponentInfo(item.name)}
                                                                        title={
                                                                            item.has_error === 2
                                                                                ? 'Загрузить информацию по компоненту'
                                                                                : 'Показать информацию'
                                                                        }
                                                                    >
                                                                        {item.has_error === 2 ? (
                                                                            <Download size={16} />
                                                                        ) : (
                                                                            <Info size={16} strokeWidth={1.5} />
                                                                        )}
                                                                    </button>
                                                                </td>

                                                                <td
                                                                    className={[
                                                                        'partNumberCell',
                                                                        item.has_error === 1 ? 'cellError' : '',
                                                                        item.has_error === 2 ? 'cellWarning' : '',
                                                                    ]
                                                                        .filter(Boolean)
                                                                        .join(' ')}
                                                                >
                                                                    {editingId === item.id ? (
                                                                        <div className="editWrapper">
                                                                            <input
                                                                                className="editInput"
                                                                                value={editingValue}
                                                                                onChange={(e) =>
                                                                                    setEditingValue(e.target.value)
                                                                                }
                                                                            />

                                                                            <button
                                                                                className="iconBtn"
                                                                                onClick={() => savePartNumber(item)}
                                                                                title="Сохранить"
                                                                            >
                                                                                <Check className="iconCheck" />
                                                                            </button>

                                                                            <button
                                                                                className="iconBtn"
                                                                                onClick={() => setEditingId(null)}
                                                                                title="Отмена"
                                                                            >
                                                                                <X className="iconClose" />
                                                                            </button>
                                                                        </div>
                                                                    ) : (
                                                                        <span
                                                                            className="editable"
                                                                            onClick={() => {
                                                                                setEditingId(item.id);
                                                                                setEditingValue(item.name ?? '');
                                                                            }}
                                                                            title="Нажмите, чтобы изменить"
                                                                        >
                                                                            {item.name}
                                                                            <Pencil className="editHintIcon" size={13} aria-hidden="true" />
                                                                        </span>
                                                                    )}
                                                                    {catalogLinks && <CatalogRowPreview partNumber={item.name} manufacturer={item.wrk} materials={catalogAssets.materials[item.name?.trim()]} />}{!catalogLinks && <CatalogAssetIcons key={item.name} partNumber={item.name} materials={catalogAssets.materials[item.name?.trim()]} />}
                                                                </td>

                                                                <td>{item.wrk}</td>
                                                                <td>{item.dc}</td>
                                                                <td>{item.lt}</td>
                                                                <td>{item.cnt}</td>
                                                                <td>{formatPrice(item.price)}</td>
                                                                <td><RemarkText text={item.remark} /></td>
                                                                <td>{item.overheadCosts}</td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {/* ===== MOBILE CARDS ===== */}
                                    {isMobile && (
                                        <div className="mobileCards">
                                            {filteredData.map(item => (
                                                <div
                                                    key={item.id}
                                                    ref={String(item.id) === String(highlightRowId) ? highlightedRowRef : null}
                                                    className={`card ${item.is_changed ? 'changedRow' : ''} ${String(item.id) === String(highlightRowId) ? 'selectedSearchRow' : ''}`}
                                                >
                                                    <div className="cardRow">
                                                        <span className="cardLabel">Part Number:</span>

                                                        {editingId === item.id ? (
                                                            <input
                                                                className="editInput"
                                                                value={editingValue}
                                                                onChange={(e) => setEditingValue(e.target.value)}
                                                            />
                                                        ) : (
                                                            <span>{item.name}{catalogLinks && <CatalogRowPreview partNumber={item.name} manufacturer={item.wrk} materials={catalogAssets.materials[item.name?.trim()]} />}{!catalogLinks && <CatalogAssetIcons key={item.name} partNumber={item.name} materials={catalogAssets.materials[item.name?.trim()]} />}</span>
                                                        )}
                                                    </div>

                                                    <div className="cardRow">
                                                        <span className="cardLabel">Manufacturer:</span>
                                                        <span>{item.wrk}</span>
                                                    </div>

                                                    <div className="cardRow">
                                                        <span className="cardLabel">DC:</span>
                                                        <span>{item.dc}</span>
                                                    </div>

                                                    <div className="cardRow">
                                                        <span className="cardLabel">LT:</span>
                                                        <span>{item.lt}</span>
                                                    </div>

                                                    <div className="cardRow">
                                                        <span className="cardLabel">Quantity:</span>
                                                        <span>{item.cnt}</span>
                                                    </div>

                                                    <div className="cardRow">
                                                        <span className="cardLabel">Price:</span>
                                                        <span>{formatPrice(item.price)}</span>
                                                    </div>

                                                    <div className="cardRow">
                                                        <span className="cardLabel">Remark:</span>
                                                        <span><RemarkText text={item.remark} /></span>
                                                    </div>

                                                    <button
                                                        className="cardInfoBtn"
                                                        onClick={() => fetchComponentInfo(item.name)}
                                                    >
                                                        {item.has_error === 2 ? 'Загрузить информацию' : 'Показать информацию'}
                                                    </button>

                                                    <div className="cardButtons">
                                                        {editingId === item.id ? (
                                                            <>
                                                                <button
                                                                    className="iconBtn"
                                                                    onClick={() => savePartNumber(item)}
                                                                    title="Сохранить"
                                                                >
                                                                    <Check className="iconCheck" />
                                                                </button>
                                                                <button
                                                                    className="iconBtn"
                                                                    onClick={() => setEditingId(null)}
                                                                    title="Отмена"
                                                                >
                                                                    <X className="iconClose" />
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <button
                                                                className="iconBtn"
                                                                onClick={() => {
                                                                    setEditingId(item.id);
                                                                    setEditingValue(item.name ?? '');
                                                                }}
                                                                title="Редактировать"
                                                            >
                                                                ✎
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ================= INFO MODAL ================= */}
            {selectedComponent && (
                <div
                    className="modal_backdrop"
                    onClick={() => setSelectedComponent(null)}
                >
                    <div
                        className="infoModal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* ===== HEADER ===== */}
                        <div className="infoModalHeader">
                            <h2 className="infoTitle">
                                Информация о компоненте: {selectedComponent}
                            </h2>


                            <button
                                data-modal-close
                                className="infoCloseBtn"
                                onClick={() => setSelectedComponent(null)}
                                title="Закрыть"
                                aria-label="Закрыть"
                            >
                                ✕
                            </button>
                        </div>

                        {/* ===== BODY ===== */}
                        {infoLoading ? (
                            <div className="loadingContainer">
                                <div className="spinner" />
                                Загрузка...
                            </div>
                        ) : (
                            (
                                <ComponentCard
                                    component={{ ...componentInfo, part_number: componentInfo?.part_number || selectedComponent }}
                                    onRefresh={refreshComponentData}
                                    refreshLoading={refreshLoading}
                                />
                            )
                        )}
                    </div>
                </div>
            )}

        </>
    );
};

export default DetailOffer;
